import React, { useEffect, useState, useRef, useCallback } from "react";
import { api, resolveImage, formatCurrency } from "../../lib/api";
import { Reorder } from "framer-motion";
import { Plus, Pencil, Trash2, Upload, Loader2, X, GripVertical, Star, Link2, Copy, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

const empty = { name: "", description: "", price: "", image_url: "", in_stock: true, is_featured: false, is_private: false, stock_count: "" };

export default function ProductsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [reorderSaving, setReorderSaving] = useState(false);
  const fileInputRef = useRef(null);
  const reorderTimer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/products");
      setItems(data);
    } catch {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const persistOrder = useCallback(async (newItems) => {
    if (reorderTimer.current) clearTimeout(reorderTimer.current);
    reorderTimer.current = setTimeout(async () => {
      setReorderSaving(true);
      try {
        await api.put("/admin/products/reorder", {
          product_ids: newItems.map((i) => i.id),
        });
      } catch {
        toast.error("Could not save new order");
      } finally {
        setReorderSaving(false);
      }
    }, 600);
  }, []);

  const handleReorder = (newItems) => {
    setItems(newItems);
    persistOrder(newItems);
  };

  const openCreate = () => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name,
      description: p.description,
      price: String(p.price),
      image_url: p.image_url,
      in_stock: p.in_stock !== false,
      is_featured: !!p.is_featured,
      is_private: !!p.is_private,
      stock_count: p.stock_count == null ? "" : String(p.stock_count),
    });
    setOpen(true);
  };

  const update = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [k]: v }));
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image too large (max 5MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((prev) => ({ ...prev, image_url: data.public_url }));
      toast.success("Image uploaded");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const stockTrimmed = String(form.stock_count).trim();
      const payload = {
        name: form.name,
        description: form.description,
        price: parseFloat(form.price),
        image_url: form.image_url,
        in_stock: form.in_stock,
        is_featured: form.is_featured,
        is_private: form.is_private,
        stock_count: stockTrimmed === "" ? null : Math.max(0, parseInt(stockTrimmed, 10) || 0),
      };
      if (editing) {
        await api.put(`/admin/products/${editing.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Product created");
      }
      setOpen(false);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Product deleted");
      await load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const patchProduct = async (id, patch) => {
    try {
      await api.put(`/admin/products/${id}`, patch);
      setItems((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
    } catch {
      toast.error("Could not update");
      await load();
    }
  };

  const privateUrl = (token) => `${window.location.origin}/reserved/${token}`;

  const copyPrivateLink = async (p) => {
    if (!p.private_token) {
      toast.error("Private link not yet generated");
      return;
    }
    const url = privateUrl(p.private_token);
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Private link copied to clipboard");
    } catch {
      toast.error("Could not copy — link: " + url);
    }
  };

  const rotateToken = async (p) => {
    if (!confirm(`Generate a new private link for "${p.name}"?\n\nThe OLD link will stop working — anyone you already sent it to will need the new one.`)) return;
    try {
      const { data } = await api.post(`/admin/products/${p.id}/rotate-token`);
      setItems((prev) => prev.map((x) => (x.id === p.id ? { ...x, is_private: true, private_token: data.private_token } : x)));
      toast.success("New private link generated");
    } catch {
      toast.error("Could not generate new link");
    }
  };

  return (
    <div data-testid="products-tab">
      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-8">
        <div>
          <p className="font-script text-2xl" style={{ color: "var(--text-accent)" }}>
            ready to ship
          </p>
          <h1 className="font-display text-4xl" style={{ color: "var(--text-primary)" }}>
            Products
          </h1>
          <p className="text-xs mt-1 flex items-center gap-2" style={{ color: "var(--text-secondary)" }}>
            <GripVertical size={12} />
            Drag the handle to reorder · featured items always show first on the homepage
            {reorderSaving && <span className="font-script">· saving order…</span>}
          </p>
        </div>
        <button
          onClick={openCreate}
          className="btn-sage inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold"
          data-testid="add-product-button"
        >
          <Plus size={16} />
          Add Product
        </button>
      </header>

      {loading ? (
        <p className="font-script text-xl" style={{ color: "var(--text-secondary)" }}>
          loading products…
        </p>
      ) : items.length === 0 ? (
        <p style={{ color: "var(--text-secondary)" }}>No products yet — add your first one!</p>
      ) : (
        <Reorder.Group
          axis="y"
          values={items}
          onReorder={handleReorder}
          className="space-y-3 max-w-3xl"
          data-testid="products-reorder-group"
        >
          {items.map((p) => (
            <Reorder.Item
              key={p.id}
              value={p}
              className="bg-white rounded-2xl overflow-hidden flex items-stretch"
              style={{ border: "1px solid var(--border-soft)" }}
              data-testid={`admin-product-${p.id}`}
            >
              <div
                className="flex items-center px-2 cursor-grab active:cursor-grabbing select-none"
                style={{ color: "var(--text-secondary)" }}
                data-testid={`drag-handle-${p.id}`}
                aria-label="Drag to reorder"
              >
                <GripVertical size={18} />
              </div>
              <div className="w-24 h-24 sm:w-28 sm:h-28 flex-shrink-0 bg-[var(--bg-secondary)]">
                <img src={resolveImage(p.image_url)} alt={p.name} className="w-full h-full object-cover" />
              </div>
              <div className="p-3 sm:p-4 flex-1 flex flex-col min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="font-display text-lg leading-tight truncate" style={{ color: "var(--text-primary)" }}>
                      {p.name}
                    </h3>
                    <p className="text-xs mt-0.5 line-clamp-1" style={{ color: "var(--text-secondary)" }}>
                      {p.description}
                    </p>
                  </div>
                  <button
                    onClick={() => patchProduct(p.id, { is_featured: !p.is_featured })}
                    className="flex-shrink-0 p-1.5 rounded-full"
                    style={{ color: p.is_featured ? "var(--brand-orange)" : "var(--text-secondary)" }}
                    aria-label={p.is_featured ? "Unfeature" : "Feature"}
                    title={p.is_featured ? "Featured — pinned to top" : "Pin to top of homepage"}
                    data-testid={`toggle-featured-${p.id}`}
                  >
                    <Star size={18} fill={p.is_featured ? "currentColor" : "none"} />
                  </button>
                </div>
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  <span className="font-semibold text-sm" style={{ color: "var(--brand-sage)" }}>
                    {formatCurrency(p.price)}
                  </span>
                  <button
                    onClick={() => patchProduct(p.id, { in_stock: !p.in_stock })}
                    className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full"
                    style={{
                      backgroundColor: p.in_stock ? "rgba(74,93,78,0.12)" : "rgba(232,163,124,0.2)",
                      color: p.in_stock ? "var(--brand-sage)" : "var(--brand-orange)",
                    }}
                    data-testid={`toggle-stock-${p.id}`}
                  >
                    {p.in_stock ? "In Stock" : "Sold Out"}
                  </button>
                  {p.stock_count != null && (
                    <span
                      className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full"
                      style={{
                        backgroundColor: p.stock_count <= 2 ? "rgba(232,163,124,0.18)" : "var(--bg-secondary)",
                        color: p.stock_count <= 2 ? "var(--brand-orange)" : "var(--text-primary)",
                        border: "1px solid var(--border-soft)",
                      }}
                      data-testid={`stock-count-${p.id}`}
                    >
                      {p.stock_count} left
                    </span>
                  )}
                  {p.is_featured && (
                    <span
                      className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                      style={{ backgroundColor: "rgba(232,163,124,0.18)", color: "var(--brand-orange)" }}
                    >
                      <Star size={9} fill="currentColor" />
                      Featured
                    </span>
                  )}
                  {p.is_private && (
                    <span
                      className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                      style={{ backgroundColor: "rgba(122,90,170,0.15)", color: "#6b4ea5" }}
                      data-testid={`private-badge-${p.id}`}
                      title="Hidden from public storefront — only accessible via private link"
                    >
                      <Link2 size={9} />
                      Private
                    </span>
                  )}
                </div>
                <div className="flex gap-2 mt-2 flex-wrap">
                  <button
                    onClick={() => openEdit(p)}
                    className="inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold"
                    style={{ border: "1px solid var(--border-soft)", color: "var(--text-primary)" }}
                    data-testid={`edit-product-${p.id}`}
                  >
                    <Pencil size={12} /> Edit
                  </button>
                  {p.is_private && p.private_token && (
                    <>
                      <button
                        onClick={() => copyPrivateLink(p)}
                        className="inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold"
                        style={{ border: "1px solid rgba(122,90,170,0.4)", color: "#6b4ea5", backgroundColor: "rgba(122,90,170,0.08)" }}
                        data-testid={`copy-private-link-${p.id}`}
                        title="Copy the private purchase link for this customer"
                      >
                        <Copy size={12} /> Copy link
                      </button>
                      <button
                        onClick={() => rotateToken(p)}
                        className="inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold"
                        style={{ border: "1px solid var(--border-soft)", color: "var(--text-secondary)" }}
                        data-testid={`rotate-private-link-${p.id}`}
                        title="Invalidate the old link and generate a new one"
                      >
                        <RefreshCw size={12} />
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => remove(p)}
                    className="inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold"
                    style={{ border: "1px solid var(--border-soft)", color: "var(--brand-orange)" }}
                    data-testid={`delete-product-${p.id}`}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            </Reorder.Item>
          ))}
        </Reorder.Group>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-lg max-h-[90vh] overflow-y-auto"
          style={{ backgroundColor: "var(--bg-primary)" }}
          data-testid="product-editor-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-2xl" style={{ color: "var(--text-primary)" }}>
              {editing ? "Edit Product" : "New Product"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={save} className="space-y-4 mt-2">
            <div>
              <label className="text-sm font-semibold block mb-1.5">Product Image</label>
              {form.image_url ? (
                <div className="relative">
                  <img
                    src={resolveImage(form.image_url)}
                    alt="preview"
                    className="w-full h-48 object-cover rounded-xl"
                    style={{ border: "1px solid var(--border-soft)" }}
                  />
                  <button
                    type="button"
                    onClick={() => setForm((p) => ({ ...p, image_url: "" }))}
                    className="absolute top-2 right-2 bg-white rounded-full p-1.5 shadow"
                    aria-label="Remove image"
                    data-testid="clear-image-button"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="w-full h-48 rounded-xl stitched flex flex-col items-center justify-center text-sm gap-2 disabled:opacity-60"
                  style={{ color: "var(--text-secondary)" }}
                  data-testid="upload-image-button"
                >
                  {uploading ? <Loader2 size={20} className="animate-spin" /> : <Upload size={20} />}
                  {uploading ? "Uploading…" : "Click to upload (jpg/png, max 5MB)"}
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                hidden
                onChange={onUpload}
                data-testid="product-image-file-input"
              />
            </div>

            <div>
              <label className="text-sm font-semibold block mb-1.5">Name</label>
              <input
                required
                value={form.name}
                onChange={update("name")}
                className="w-full px-4 py-2 rounded-xl outline-none"
                style={{ backgroundColor: "#fff", border: "1px solid var(--border-soft)" }}
                data-testid="product-name-input"
              />
            </div>

            <div>
              <label className="text-sm font-semibold block mb-1.5">Description</label>
              <textarea
                required
                rows={3}
                value={form.description}
                onChange={update("description")}
                className="w-full px-4 py-2 rounded-xl outline-none resize-none"
                style={{ backgroundColor: "#fff", border: "1px solid var(--border-soft)" }}
                data-testid="product-description-input"
              />
            </div>

            <div>
              <label className="text-sm font-semibold block mb-1.5">Price (USD)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                required
                value={form.price}
                onChange={update("price")}
                className="w-full px-4 py-2 rounded-xl outline-none"
                style={{ backgroundColor: "#fff", border: "1px solid var(--border-soft)" }}
                data-testid="product-price-input"
              />
            </div>

            <div>
              <label className="text-sm font-semibold block mb-1.5">
                Stock count
                <span className="ml-2 font-normal text-xs" style={{ color: "var(--text-secondary)" }}>
                  (leave blank for unlimited)
                </span>
              </label>
              <input
                type="number"
                step="1"
                min="0"
                placeholder="e.g. 5"
                value={form.stock_count}
                onChange={update("stock_count")}
                className="w-full px-4 py-2 rounded-xl outline-none"
                style={{ backgroundColor: "#fff", border: "1px solid var(--border-soft)" }}
                data-testid="product-stock-count-input"
              />
              <p className="text-xs mt-1.5" style={{ color: "var(--text-secondary)" }}>
                Each sale subtracts from this number automatically. When it reaches 0, the item is hidden from the homepage.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
              <label className="flex items-center gap-2 text-sm font-semibold">
                <input
                  type="checkbox"
                  checked={form.in_stock}
                  onChange={update("in_stock")}
                  data-testid="product-in-stock-checkbox"
                />
                In stock
              </label>
              <label className="flex items-center gap-2 text-sm font-semibold">
                <input
                  type="checkbox"
                  checked={form.is_featured}
                  onChange={update("is_featured")}
                  data-testid="product-featured-checkbox"
                />
                <Star size={14} fill={form.is_featured ? "var(--brand-orange)" : "none"} style={{ color: "var(--brand-orange)" }} />
                Feature on homepage (pin to top)
              </label>
            </div>

            <div
              className="rounded-xl p-3"
              style={{ border: "1px solid rgba(122,90,170,0.2)", backgroundColor: "rgba(122,90,170,0.05)" }}
            >
              <label className="flex items-start gap-2 text-sm font-semibold cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_private}
                  onChange={update("is_private")}
                  className="mt-0.5"
                  data-testid="product-private-checkbox"
                />
                <span>
                  <span className="inline-flex items-center gap-1" style={{ color: "#6b4ea5" }}>
                    <Link2 size={14} />
                    Reserved Item (private link)
                  </span>
                  <p className="text-xs mt-1 font-normal" style={{ color: "var(--text-secondary)" }}>
                    Hides this product from the public storefront. A private URL will be generated that you can share with one specific customer — only people with the link can view or buy it.
                  </p>
                </span>
              </label>
            </div>

            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="flex-1 py-2.5 rounded-full text-sm font-semibold"
                style={{ border: "1px solid var(--border-soft)", color: "var(--text-primary)" }}
                data-testid="cancel-product-button"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving || uploading || !form.image_url}
                className="btn-sage flex-1 py-2.5 rounded-full text-sm font-semibold disabled:opacity-60"
                data-testid="save-product-button"
              >
                {saving ? "Saving…" : editing ? "Save changes" : "Create product"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
